import React, { useState, useEffect, useCallback } from 'react';
import { Card, Select, Spin, message, Statistic, Row, Col, Table, Button, Radio, Tag } from 'antd';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup } from 'react-leaflet';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import api from '../services/api';
import { playerIcon } from '../components/community/mapIcons';
import 'leaflet/dist/leaflet.css';

const { Option } = Select;

const PlayersCoverageMap = () => {
  const [loading, setLoading] = useState(true);
  const [ddds, setDdds] = useState(null);      // dados agregados por DDD (fonte "celulares")
  const [community, setCommunity] = useState(null); // community_players (fonte "comunidade")
  const [source, setSource] = useState('comunidade'); // 'comunidade' | 'celulares'
  const [viewMode, setViewMode] = useState('state'); // pro modo celulares
  const [selectedState, setSelectedState] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [dc, cc] = await Promise.all([
        api.get('/coverage-map/players'),
        api.get('/coverage-map/community-players'),
      ]);
      setDdds(dc.data);
      setCommunity(cc.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      message.error('Erro ao carregar dados do mapa');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>;
  }
  if (!ddds || !community) return null;

  // --- Fonte "celulares" (agregação DDDs, comportamento antigo) ---
  const filteredPlayers = selectedState
    ? ddds.players.filter(p => p.state === selectedState)
    : ddds.players;

  const markers = viewMode === 'state'
    ? ddds.byState.map(state => {
        const firstDDD = state.ddds[0];
        const cityData = ddds.byDDD.find(d => d.ddd === firstDDD);
        return { ...state, lat: cityData?.lat || 0, lng: cityData?.lng || 0, label: state.state };
      })
    : ddds.byDDD.map(ddd => ({ ...ddd, label: `${ddd.city} (${ddd.ddd})` }));

  const cellColumns = [
    { title: 'Jogador', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
    { title: 'Time', dataIndex: 'team_name', sorter: (a, b) => a.team_name.localeCompare(b.team_name) },
    { title: 'Estado', dataIndex: 'state',
      filters: [...new Set(ddds.players.map(p => p.state))].map(s => ({ text: s, value: s })),
      onFilter: (value, record) => record.state === value },
    { title: 'Cidade', dataIndex: 'city', sorter: (a, b) => a.city.localeCompare(b.city) },
    { title: 'DDD', dataIndex: 'ddd', sorter: (a, b) => a.ddd - b.ddd },
  ];

  // --- Fonte "comunidade" (community_players com lat/lng reais) ---
  const communityColumns = [
    { title: 'Jogador', dataIndex: 'name', sorter: (a, b) => (a.name||'').localeCompare(b.name||'') },
    { title: 'Cidade', dataIndex: 'city' },
    { title: 'Estado', dataIndex: 'state', width: 80 },
    { title: 'Posição', dataIndex: 'preferred_position', width: 110,
      render: v => v === 'goleiro' ? <Tag color="gold">Goleiro</Tag> : v ? <Tag color="blue">{v}</Tag> : '-' },
    { title: 'Nível', dataIndex: 'skill_level', width: 110,
      render: v => v || '-' },
    { title: 'Público', dataIndex: 'is_public', width: 90,
      render: v => v ? <Tag color="green">Sim</Tag> : <Tag>Não</Tag> },
    { title: 'Origem', dataIndex: 'auth_provider', width: 100,
      render: v => v === 'google' ? <Tag color="blue">Google</Tag> : <Tag>{v || 'form'}</Tag> },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1>Mapa de Cobertura — Jogadores</h1>

      {/* Toggle de fonte */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Radio.Group value={source} onChange={e => { setSource(e.target.value); setSelectedState(null); }}>
              <Radio.Button value="comunidade">Cadastrados na comunidade</Radio.Button>
              <Radio.Button value="celulares">Cobertura por DDD (celulares)</Radio.Button>
            </Radio.Group>
          </Col>
          {source === 'celulares' && (
            <Col>
              <span style={{ marginRight: 8 }}>Visualização:</span>
              <Select value={viewMode} onChange={setViewMode} style={{ width: 160 }}>
                <Option value="state">Por Estado</Option>
                <Option value="city">Por Cidade</Option>
              </Select>
            </Col>
          )}
        </Row>
      </Card>

      {/* Stats de topo */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {source === 'comunidade' ? (
          <>
            <Col span={8}><Card><Statistic title="Jogadores cadastrados" value={community.summary.total} /></Card></Col>
            <Col span={8}><Card><Statistic title="Públicos (aparecem no app)" value={community.summary.publicos} /></Card></Col>
            <Col span={8}><Card><Statistic title="Goleiros" value={community.summary.goleiros} /></Card></Col>
          </>
        ) : (
          <>
            <Col span={8}><Card><Statistic title="Estados com Jogadores" value={ddds.summary.totalStates} /></Card></Col>
            <Col span={8}><Card><Statistic title="Cidades com Jogadores" value={ddds.summary.totalCities} /></Card></Col>
            <Col span={8}><Card><Statistic title="Jogadores com Telefone Válido" value={ddds.summary.totalPlayers} /></Card></Col>
          </>
        )}
      </Row>

      {/* Mapa */}
      <Card
        style={{
          marginBottom: 24,
          ...(isFullscreen && { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, margin: 0, borderRadius: 0, height: '100vh' })
        }}
        title={isFullscreen ? (source === 'comunidade' ? 'Jogadores cadastrados' : 'Cobertura por DDD') : null}
        extra={
          <Button icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={() => setIsFullscreen(!isFullscreen)}>
            {isFullscreen ? 'Sair do Fullscreen' : 'Fullscreen'}
          </Button>
        }
      >
        <MapContainer
          center={[-15.7801, -47.9292]}
          zoom={4}
          maxBounds={[[-35, -75], [5, -30]]}
          minZoom={4}
          style={{ height: isFullscreen ? 'calc(100vh - 60px)' : 600, width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          {source === 'comunidade' && community.players.map(p => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={playerIcon({ isGoalkeeper: p.preferred_position === 'goleiro', photoUrl: p.profile_photo, zoom: 14 })}
            >
              <Popup>
                <div style={{ minWidth: 220 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    {p.city ? `${p.city}${p.state ? ` / ${p.state}` : ''}` : (p.state || '—')}
                    {p.neighborhood && ` · ${p.neighborhood}`}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    {p.preferred_position === 'goleiro' ? <Tag color="gold">Goleiro</Tag> : p.preferred_position ? <Tag color="blue">{p.preferred_position}</Tag> : null}
                    {p.skill_level && <Tag>{p.skill_level}</Tag>}
                    {p.is_public ? <Tag color="green">Público</Tag> : <Tag>Privado</Tag>}
                  </div>
                  {p.phone && <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{p.phone}</div>}
                </div>
              </Popup>
            </Marker>
          ))}

          {source === 'celulares' && markers.map((marker, index) => (
            <CircleMarker
              key={index}
              center={[marker.lat, marker.lng]}
              radius={Math.max(10, Math.min(marker.count * 0.5, 50))}
              fillColor="#1890ff"
              color="#fff"
              weight={2}
              opacity={1}
              fillOpacity={0.6}
              eventHandlers={{ click: () => setSelectedState(marker.state) }}
            >
              <Popup>
                <div>
                  <h3>{marker.label}</h3>
                  {viewMode === 'state' ? (
                    <>
                      <p><strong>DDDs:</strong> {marker.ddds.join(', ')}</p>
                      <p><strong>Total de Jogadores:</strong> {marker.count}</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Estado:</strong> {marker.state}</p>
                      <p><strong>Cidade:</strong> {marker.city}</p>
                      <p><strong>Total de Jogadores:</strong> {marker.count}</p>
                    </>
                  )}
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Card>

      {/* Tabela */}
      <Card title={source === 'comunidade' ? 'Jogadores cadastrados na comunidade' : (selectedState ? `Jogadores do Estado: ${selectedState}` : 'Lista de Jogadores')}
        extra={source === 'celulares' && selectedState && <a onClick={() => setSelectedState(null)}>Limpar filtro</a>}>
        {source === 'comunidade' ? (
          <Table columns={communityColumns} dataSource={community.players} rowKey="id" pagination={{ pageSize: 20 }} />
        ) : (
          <Table columns={cellColumns} dataSource={filteredPlayers} rowKey="id" pagination={{ pageSize: 20 }} />
        )}
      </Card>
    </div>
  );
};

export default PlayersCoverageMap;
